import { useBannerState } from '../components/BannerManager';

export function useBannerPositioning() {
  const { bannerCount } = useBannerState();

  // Calculate positions based on active banners
  // Note: Mobile layout differs from desktop - navbar is hidden on mobile,
  // so content needs extra padding to account for fixed logo/toggle
  const getTopPosition = (element: 'dropdown' | 'logo' | 'content' | 'sidebar') => {
    // When three or more banners are active
    if (bannerCount >= 3) {
      switch (element) {
        case 'dropdown':
          return 'top-28 sm:top-[6.75rem]';
        case 'logo':
          return 'top-[7rem] sm:top-[7.5rem]';
        case 'content':
          return 'pt-40 sm:pt-36 lg:pt-28 lg:sm:pt-[7.75rem]';
        case 'sidebar':
          return 'top-24 sm:top-[6.75rem]';
      }
    }

    // When two banners are active
    if (bannerCount === 2) {
      switch (element) {
        case 'dropdown':
          return 'top-20 sm:top-[4.5rem]';
        case 'logo':
          return 'top-[4.75rem] sm:top-[5.25rem]';
        case 'content':
          // Mobile needs more padding since logo/toggle are fixed over content
          return 'pt-32 sm:pt-28 lg:pt-20 lg:sm:pt-[5.5rem]';
        case 'sidebar':
          return 'top-16 sm:top-[4.5rem]';
      }
    }

    // When only one banner is active
    if (bannerCount === 1) {
      switch (element) {
        case 'dropdown':
          return 'top-12 sm:top-[3.25rem]';
        case 'logo':
          return 'top-11 sm:top-12';
        case 'content':
          // Mobile needs more padding since logo/toggle are fixed over content
          return 'pt-24 sm:pt-20 lg:pt-12 lg:sm:pt-[3.25rem]';
        case 'sidebar':
          return 'top-8 sm:top-9';
      }
    }

    // When no banners are active
    switch (element) {
      case 'dropdown':
        return 'top-4';
      case 'logo':
        return 'top-3';
      case 'content':
        return 'pt-20 lg:pt-3'; // More padding on mobile for fixed header
      case 'sidebar':
        return 'top-0';
    }
  };

  const getSidebarSpacerHeight = () => {
    if (bannerCount >= 3) return 'h-28 sm:h-[6.75rem]';
    if (bannerCount === 2) return 'h-20 sm:h-[4.5rem]';
    if (bannerCount === 1) return 'h-16 sm:h-[3.75rem]';
    return 'h-24';
  };

  return {
    dropdownPosition: getTopPosition('dropdown'),
    logoPosition: getTopPosition('logo'),
    contentPosition: getTopPosition('content'),
    sidebarPosition: getTopPosition('sidebar'),
    sidebarSpacerHeight: getSidebarSpacerHeight(),
    hasTopBanner: bannerCount > 0,
  };
}
